<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import addDays from 'date-fns/addDays';
import endOfDay from 'date-fns/endOfDay';
import format from 'date-fns/format';
import fromUnixTime from 'date-fns/fromUnixTime';
import getUnixTime from 'date-fns/getUnixTime';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import { useI18n } from 'vue-i18n';
import { STATUS } from 'dashboard/store/constants';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';

const LAST_7_DAYS_OFFSET = 6;
const METRIC_KEY = 'conversations_count';

const store = useStore();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const accountSummary = useMapGetter('getAccountSummary');
const accountReports = useMapGetter('getAccountReports');
const currentUser = useMapGetter('getCurrentUser');
const accountSummaryFetchingStatus = useMapGetter(
  'getAccountSummaryFetchingStatus'
);

const selectedStatsScope = ref('all');
const lastGeneratedAt = ref(null);

const dateRange = computed(() => {
  const toDate = endOfDay(new Date());
  const fromDate = startOfDay(subDays(toDate, LAST_7_DAYS_OFFSET));
  return {
    from: getUnixTime(fromDate),
    to: getUnixTime(toDate),
  };
});

const reportPayload = computed(() => {
  const payload = {
    from: dateRange.value.from,
    to: dateRange.value.to,
    type: 'account',
    groupBy: 'day',
    businessHours: false,
  };
  if (selectedStatsScope.value === 'mine' && currentUser.value?.id) {
    payload.type = 'agent';
    payload.id = currentUser.value.id;
  }
  return payload;
});

const conversationTimeline = computed(
  () => accountReports.value?.data?.[METRIC_KEY] || []
);

const totalChats = computed(() =>
  Number(accountSummary.value?.conversations_count || 0)
);

const previousTotalChats = computed(() =>
  Number(accountSummary.value?.previous?.conversations_count || 0)
);

const totalChatsDiff = computed(
  () => totalChats.value - previousTotalChats.value
);

const diffIsNegative = computed(() => totalChatsDiff.value < 0);

const absDiff = computed(() => Math.abs(totalChatsDiff.value));

const hasChartData = computed(() => conversationTimeline.value.length > 0);

const isLoading = computed(
  () =>
    accountSummaryFetchingStatus.value === STATUS.FETCHING ||
    accountReports.value?.isFetching?.[METRIC_KEY]
);

const currentPeriod = computed(() => {
  const toDate = endOfDay(new Date());
  const fromDate = startOfDay(subDays(toDate, LAST_7_DAYS_OFFSET));
  return { fromDate, toDate };
});

const dayRows = computed(() => {
  return Array.from({ length: 7 }, (_, index) => {
    const date = addDays(currentPeriod.value.fromDate, index);
    const ts = getUnixTime(date);
    return {
      timestamp: ts,
      dayKey: format(date, 'yyyy-MM-dd'),
      shortLabel: format(date, 'dd MMM'),
      tooltipLabel: format(date, "dd MMM''yy"),
    };
  });
});

const timelineValueMap = computed(() => {
  return (conversationTimeline.value || []).reduce((acc, item) => {
    const dayKey = format(fromUnixTime(item.timestamp), 'yyyy-MM-dd');
    acc[dayKey] = Number(item.value || 0);
    return acc;
  }, {});
});

const chartCollection = computed(() => ({
  labels: dayRows.value.map(row => row.shortLabel),
  datasets: [
    {
      label: t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
      data: dayRows.value.map(row =>
        Number(timelineValueMap.value[row.dayKey] || 0)
      ),
      backgroundColor: '#1D75E5',
      borderRadius: 4,
      categoryPercentage: 0.7,
      barPercentage: 0.9,
    },
  ],
}));

const tooltipId = 'last-7-days-tooltip';

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => dayRows.value[index]?.tooltipLabel || '',
        seriesLabelForDataset: () => t('TOTAL_CHATS_REPORT.TOTAL_CHATS'),
        showPercent: false,
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: { callback: value => Number(value).toLocaleString() },
    },
  },
}));

// Chat satisfaction from summary
const chatSatisfactionRate = computed(() => {
  const v = accountSummary.value?.csat_survey_score;
  return v != null ? Math.round(v) : null;
});

// p.p. diff for CSAT
const chatSatisfactionDiff = computed(() => {
  const cur = accountSummary.value?.csat_survey_score;
  const prev = accountSummary.value?.previous?.csat_survey_score;
  if (cur == null || prev == null) return null;
  return Math.round(cur - prev);
});

const queuedVisitors = computed(
  () => accountSummary.value?.queued_visitors_count ?? 0
);

const generatedAtLabel = computed(() => {
  if (!lastGeneratedAt.value) return t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET');
  const d = lastGeneratedAt.value;
  return d.toLocaleString('en-US', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  });
});

const fetchData = () => {
  const payload = reportPayload.value;
  store.dispatch('fetchAccountSummary', payload);
  store.dispatch('fetchAccountReport', { metric: METRIC_KEY, ...payload });
  lastGeneratedAt.value = new Date();
};

watch(selectedStatsScope, fetchData);
watch(
  () => currentUser.value?.id,
  id => {
    if (!id && selectedStatsScope.value === 'mine') {
      selectedStatsScope.value = 'all';
      return;
    }
    if (selectedStatsScope.value === 'mine') fetchData();
  }
);

onMounted(fetchData);

onBeforeUnmount(() => {
  removeTooltipEl(tooltipId);
});
</script>

<template>
  <div class="l7d-page">
    <!-- Top header bar -->
    <div class="l7d-header">
      <span class="l7d-header__title">Last 7 days</span>
    </div>

    <!-- Scrollable content -->
    <div class="l7d-content">
      <!-- Total Chats card -->
      <div class="l7d-card">
        <!-- Card header: title + toggle -->
        <div class="l7d-card__header">
          <span class="l7d-card__label">Total chats</span>
          <div class="l7d-seg-control">
            <button
              class="l7d-seg-btn"
              :class="{ 'l7d-seg-btn--active': selectedStatsScope === 'mine' }"
              type="button"
              @click="selectedStatsScope = 'mine'"
            >
              My stats
            </button>
            <button
              class="l7d-seg-btn"
              :class="{ 'l7d-seg-btn--active': selectedStatsScope === 'all' }"
              type="button"
              @click="selectedStatsScope = 'all'"
            >
              All agents
            </button>
          </div>
        </div>

        <!-- Big number + diff badge -->
        <div class="l7d-card__stat-row">
          <div class="l7d-card__big-num">
            <span class="l7d-card__big-num-value">
              {{ totalChats.toLocaleString() }}
            </span>
            <span
              v-if="previousTotalChats || totalChatsDiff !== 0"
              class="l7d-diff-badge"
              :class="
                diffIsNegative ? 'l7d-diff-badge--neg' : 'l7d-diff-badge--pos'
              "
            >
              <span>{{ diffIsNegative ? '-' : '+' }}</span>
              <span>{{ absDiff.toLocaleString() }}</span>
              <span class="l7d-diff-badge__label">from previous 7 days</span>
            </span>
          </div>
          <RouterLink
            :to="accountScopedRoute('conversation_reports')"
            class="l7d-view-report-btn"
          >
            View report
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              width="16"
              height="16"
            >
              <path
                stroke="currentcolor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 12H5m14 0-4-4m4 4-4 4"
              />
            </svg>
          </RouterLink>
        </div>

        <!-- Bar Chart -->
        <div class="l7d-chart-wrap">
          <!-- Loading state -->
          <div v-if="isLoading" class="l7d-chart-loading">
            <div class="l7d-spinner" />
          </div>

          <!-- No data -->
          <div v-else-if="!hasChartData" class="l7d-chart-empty">
            {{ t('LAST_7_DAYS_REPORT.NO_DATA') }}
          </div>

          <div v-else class="l7d-chart">
            <BarChart
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
          </div>
        </div>

        <!-- Bottom stats row: Queued visitors | Chat satisfaction -->
        <div class="l7d-stats-row">
          <!-- Queued visitors -->
          <div class="l7d-stat-cell l7d-stat-cell--border-right">
            <div class="l7d-stat-inner">
              <svg
                class="l7d-stat-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M8 7a4 4 0 1 0 8 0 4 4 0 0 0-8 0M6 21v-2a4 4 0 0 1 4-4h1M17 14v4h4"
                />
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 18a4 4 0 1 0 8 0 4 4 0 0 0-8 0"
                />
              </svg>
              <span class="l7d-stat-label">Queued visitors</span>
              <span class="l7d-stat-value">{{ queuedVisitors }}</span>
            </div>
          </div>

          <!-- Chat satisfaction -->
          <div class="l7d-stat-cell">
            <div class="l7d-stat-inner">
              <svg
                class="l7d-stat-icon"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentcolor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m13.655 3.39-3.7 4.858c-.331.324-.519.772-.519 1.237v8.761c0 .965.805 1.754 1.788 1.754h5.006a1.78 1.78 0 0 0 1.635-1.061l2.914-6.674c.76-1.737-.536-3.666-2.458-3.666h-3.263l.85-4.017a1.3 1.3 0 0 0-.367-1.202 1.35 1.35 0 0 0-1.886.01M4.788 20c.983 0 1.788-.79 1.788-1.754V11.23c0-.965-.805-1.754-1.788-1.754-.984 0-1.788.789-1.788 1.754v7.016C3 19.211 3.804 20 4.788 20"
                />
              </svg>
              <span class="l7d-stat-label">Chat satisfaction</span>
              <div class="l7d-stat-csat">
                <span
                  v-if="chatSatisfactionDiff !== null"
                  class="l7d-csat-diff"
                  :class="
                    chatSatisfactionDiff >= 0
                      ? 'l7d-csat-diff--pos'
                      : 'l7d-csat-diff--neg'
                  "
                >
                  <span
                    >{{ chatSatisfactionDiff >= 0 ? '+' : ''
                    }}{{ chatSatisfactionDiff }} p. p.</span
                  >
                </span>
                <span class="l7d-stat-value">
                  {{
                    chatSatisfactionRate !== null
                      ? `${chatSatisfactionRate}%`
                      : '—'
                  }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Generated at footer -->
      <p class="l7d-footer">
        This report was generated on {{ generatedAtLabel }}<br />
        All reports are displayed in your local time.
      </p>
    </div>
  </div>
</template>

<style scoped>
/* Force white theme regardless of dark/light mode */
.l7d-page,
.dark .l7d-page {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  width: 100%;
  height: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  overflow-y: auto;
  overflow-x: hidden;
  background: #ffffff !important;
  color: #111827;
  font-family:
    Inter,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    sans-serif;
}

/* ─── Header ─── */
.l7d-header {
  display: flex;
  align-items: center;
  height: 56px;
  min-height: 56px;
  padding: 0 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  box-sizing: border-box;
  flex-shrink: 0;
}

.l7d-header__title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  letter-spacing: 0.3px;
}

/* ─── Scrollable content ─── */
.l7d-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 32px 24px 32px;
  background: #ffffff !important;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
}

.l7d-content::after {
  content: '';
  display: block;
  min-height: 48px;
  flex: 0 0 auto;
}

/* ─── Card ─── */
.l7d-card {
  flex: 0 0 auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  padding: 20px;
  box-sizing: border-box;
}

/* Card header row */
.l7d-card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 40px;
  margin-bottom: 4px;
}

.l7d-card__label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  line-height: 21px;
}

/* Segmented control */
.l7d-seg-control {
  display: inline-flex;
  flex-direction: row;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: #ffffff;
  padding: 3px;
}

.l7d-seg-btn {
  height: 28px;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition:
    background 0.15s,
    color 0.15s;
  line-height: 28px;
}

.l7d-seg-btn + .l7d-seg-btn {
  margin-left: 5px;
}

.l7d-seg-btn--active {
  background: #e5e7eb;
  color: #111827;
}

/* Big number row */
.l7d-card__stat-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding-top: 8px;
  margin-bottom: 4px;
}

.l7d-card__big-num {
  display: flex;
  align-items: center;
  gap: 0;
  flex-wrap: wrap;
}

.l7d-card__big-num-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  line-height: 28px;
  min-width: 120px;
}

/* Diff badge */
.l7d-diff-badge {
  display: inline-flex;
  align-items: center;
  gap: 0;
  margin-left: 8px;
  padding: 0 4px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  line-height: 20px;
  background: #f3f4f6;
  color: #111827;
}

.l7d-diff-badge--neg {
  color: #dc2626;
}

.l7d-diff-badge--pos {
  color: #059669;
}

.l7d-diff-badge__label {
  font-size: 13px;
  font-weight: 400;
  color: #6b7280;
  margin-left: 4px;
}

/* View report button */
.l7d-view-report-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  text-decoration: underline;
  text-underline-position: from-font;
  cursor: pointer;
  white-space: nowrap;
  padding: 0;
  background: none;
  border: none;
  visibility: hidden;
}

/* No hover-reveal effects */

/* ─── Bar Chart ─── */
.l7d-chart-wrap {
  position: relative;
  width: 100%;
  height: 200px;
  margin-top: 12px;
  margin-bottom: 0;
}

.l7d-chart-loading,
.l7d-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #9ca3af;
  font-size: 13px;
}

.l7d-chart {
  height: 200px;
}

.l7d-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: l7d-spin 0.8s linear infinite;
}

@keyframes l7d-spin {
  to {
    transform: rotate(360deg);
  }
}

/* ─── Bottom stats row ─── */
.l7d-stats-row {
  display: flex;
  flex-wrap: wrap;
  margin-top: 32px;
}

.l7d-stat-cell {
  display: flex;
  align-items: center;
  box-sizing: border-box;
  margin-bottom: 24px;
  width: 50%;
}

.l7d-stat-cell--border-right {
  border-right: 1px solid #e5e7eb;
  padding-right: 32px;
}

.l7d-stat-cell:not(.l7d-stat-cell--border-right) {
  padding-left: 32px;
}

.l7d-stat-inner {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 0;
}

.l7d-stat-icon {
  width: 20px;
  height: 20px;
  color: #111827;
  flex-shrink: 0;
}

.l7d-stat-label {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  padding: 0 8px;
  flex: 1 0 auto;
}

.l7d-stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  min-height: 34px;
  display: flex;
  align-items: center;
}

/* CSAT value group */
.l7d-stat-csat {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 34px;
}

.l7d-csat-diff {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
}

.l7d-csat-diff--pos {
  color: #059669;
}

.l7d-csat-diff--neg {
  color: #dc2626;
}

/* ─── Footer ─── */
.l7d-footer {
  font-size: 13px;
  line-height: 22px;
  margin: 12px 0 0;
  padding-bottom: 24px;
  text-align: center;
  color: #9ca3af;
}

::global(body.dark) .l7d-page,
::global(body.dark) .l7d-header,
::global(body.dark) .l7d-content,
::global(body.dark) .l7d-card,
::global(body.theme-mix) .l7d-page,
::global(body.theme-mix) .l7d-header,
::global(body.theme-mix) .l7d-content,
::global(body.theme-mix) .l7d-card {
  background: #ffffff !important;
  border-color: #e5e7eb !important;
  color: #111827 !important;
}

::global(body.dark) .l7d-header__title,
::global(body.dark) .l7d-card__label,
::global(body.dark) .l7d-card__big-num-value,
::global(body.dark) .l7d-stat-label,
::global(body.dark) .l7d-stat-value,
::global(body.theme-mix) .l7d-header__title,
::global(body.theme-mix) .l7d-card__label,
::global(body.theme-mix) .l7d-card__big-num-value,
::global(body.theme-mix) .l7d-stat-label,
::global(body.theme-mix) .l7d-stat-value {
  color: #111827 !important;
}

::global(body.dark) .l7d-diff-badge,
::global(body.theme-mix) .l7d-diff-badge {
  background: #f3f4f6 !important;
  color: #111827 !important;
}

::global(body.dark) .l7d-diff-badge--neg,
::global(body.theme-mix) .l7d-diff-badge--neg {
  color: #dc2626 !important;
}

::global(body.dark) .l7d-diff-badge--pos,
::global(body.theme-mix) .l7d-diff-badge--pos {
  color: #059669 !important;
}

::global(body.dark) .l7d-diff-badge__label,
::global(body.theme-mix) .l7d-diff-badge__label {
  color: #6b7280 !important;
}

::global(body.dark) .l7d-seg-control,
::global(body.theme-mix) .l7d-seg-control {
  background: #ffffff !important;
  border-color: #d1d5db !important;
}

::global(body.dark) .l7d-seg-btn,
::global(body.theme-mix) .l7d-seg-btn {
  color: #374151 !important;
}

::global(body.dark) .l7d-seg-btn--active,
::global(body.theme-mix) .l7d-seg-btn--active {
  background: #e5e7eb !important;
  color: #111827 !important;
}

::global(body.dark) .l7d-stat-cell--border-right,
::global(body.dark) .l7d-stat-cell:nth-child(4),
::global(body.theme-mix) .l7d-stat-cell--border-right,
::global(body.theme-mix) .l7d-stat-cell:nth-child(4) {
  border-color: #e5e7eb !important;
}

::global(body.dark) .l7d-stat-add-btn,
::global(body.theme-mix) .l7d-stat-add-btn {
  background: #ffffff !important;
  border-color: #d1d5db !important;
  color: #374151 !important;
}

::global(body.dark) .l7d-footer,
::global(body.theme-mix) .l7d-footer {
  color: #9ca3af !important;
}

::global(body.dark) .l7d-stat-icon,
::global(body.theme-mix) .l7d-stat-icon {
  color: #111827 !important;
}

::global(body.dark) .l7d-header,
::global(body.theme-mix) .l7d-header {
  border-color: #e5e7eb !important;
}

::global(body.dark) .l7d-view-report-btn,
::global(body.theme-mix) .l7d-view-report-btn {
  color: #111827 !important;
}

::global(body.dark) .l7d-csat-diff--pos,
::global(body.theme-mix) .l7d-csat-diff--pos {
  color: #059669 !important;
}

::global(body.dark) .l7d-csat-diff--neg,
::global(body.theme-mix) .l7d-csat-diff--neg {
  color: #dc2626 !important;
}
</style>
